import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEnvelope, FaLock, FaEye, FaEyeSlash, FaFingerprint } from 'react-icons/fa';
import cpApi from '../utils/cpApi';
import './CPLogin.css';

const CPLogin = () => {
  const [form, setForm]       = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const navigate              = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await cpApi.post('/controlpanel/login', form);
      localStorage.setItem('cp_token', data.token);
      localStorage.setItem('cp_admin', JSON.stringify(data.admin));
      navigate('/cp/colleges', { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || 'Authentication failed. Verify credentials.');
    } finally {
      setLoading(false);
    }
  };

  const togglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <div className="cp-login-container">
      
      {/* ── Animated Tech Background & Mathematical Overview ── */}
      <div className="cp-bg-elements">
        <div className="cp-blob blob-1"></div>
        <div className="cp-blob blob-2"></div>
        <div className="cp-blob blob-3"></div>
        
        {/* Abstract mathematical representation of the panel's functions */}
        <div className="cp-math-node math-tl">
          ƒ(C) = {'{'} UUID, Authₖ, Role_Map {'}'}<br/>
          <span className="cp-math-sub">Initialize Tenant Nodes</span>
        </div>
        <div className="cp-math-node math-tr">
          ∑ (Uᵢ → {`{C₁, ..., Cₙ}`})<br/>
          <span className="cp-math-sub">Distribute Admin Matrices</span>
        </div>
        <div className="cp-math-node math-bl">
          Δ Access ≡ 256-bit AES<br/>
          <span className="cp-math-sub">Zero-Trust Protocol</span>
        </div>
        <div className="cp-math-node math-br">
          λ(x) : Clerk | Admin ↦ Node(x)<br/>
          <span className="cp-math-sub">Hierarchical Binding</span>
        </div>
      </div>

      {/* ── Centered Login Card ── */}
      <div className="cp-login-card">
        <div className="cp-login-header">
          <div className="cp-login-icon-ring">
            <FaFingerprint />
          </div>
          <h2>Control Protocol</h2>
          <p>
            Authenticate to manage infrastructure matrices, provision educational nodes, and distribute cryptographic access.
          </p>
        </div>

        {error && (
          <div className="cp-login-error">
            <span>⚠</span> {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="cp-login-form">
          <div className="cp-login-group">
            <label>Master Gateway ID (Email)</label>
            <div className="cp-login-input-wrap">
              <FaEnvelope className="cp-login-input-icon" />
              <input
                type="email"
                required
                autoFocus
                placeholder="root@system.io"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                className="cp-login-input"
              />
            </div>
          </div>

          <div className="cp-login-group">
            <label>Cryptographic Key (Password)</label>
            <div className="cp-login-input-wrap">
              <FaLock className="cp-login-input-icon" />
              <input
                type={showPassword ? "text" : "password"}
                required
                placeholder="••••••••••••"
                value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                className="cp-login-input"
              />
              <button 
                type="button" 
                className="cp-login-eye-btn" 
                onClick={togglePassword}
                tabIndex="-1"
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          <button type="submit" className="cp-login-btn" disabled={loading}>
            {loading ? (
              <><span className="cp-login-spinner" /> Verifying Hash...</>
            ) : (
              'Initialize Session'
            )}
          </button>
          
          <div className="cp-login-footer">
            Secured via End-to-End Encryption Architecture
          </div>
        </form>
      </div>

    </div>
  );
};

export default CPLogin;